import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_REGISTRY } from '@/gateway/apps';

/** The apps this shell actually ships, named for a marketing sentence. Derived
 *  from the one registry so removing or adding an app updates this copy too. */
const APP_NAMES = APP_REGISTRY.map((a) => a.name);
const APP_LIST = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(APP_NAMES);

interface Copy {
  title: ReactNode;
  body: string;
  showStats: boolean;
}

const GRADIENT_TEXT =
  'bg-[linear-gradient(90deg,rgb(var(--indigo-500)),#b06ab3)] bg-clip-text text-transparent';

function copyForPath(pathname: string): Copy {
  if (pathname.startsWith('/login')) {
    return {
      title: (
        <>
          Welcome <span className={GRADIENT_TEXT}>back.</span>
        </>
      ),
      body: 'Sign in to pick up where you left off — your workspace, apps, and team are waiting.',
      showStats: false,
    };
  }
  if (pathname.startsWith('/verify-otp')) {
    return {
      title: (
        <>
          Almost <span className={GRADIENT_TEXT}>there.</span>
        </>
      ),
      body: "Enter the 6-digit code we just sent to your email — it's good for 10 minutes.",
      showStats: false,
    };
  }
  if (pathname.startsWith('/invites')) {
    return {
      title: (
        <>
          You've been <span className={GRADIENT_TEXT}>invited.</span>
        </>
      ),
      body: 'Join your team on Clarix and start collaborating across projects, meetings, and tasks in one place.',
      showStats: false,
    };
  }
  if (pathname.startsWith('/onboarding')) {
    return {
      title: (
        <>
          Spin up your <span className={GRADIENT_TEXT}>workspace.</span>
        </>
      ),
      body: "Name your company, claim a URL, and pick a few defaults. You can polish branding, billing, and team settings the moment you're inside.",
      showStats: true,
    };
  }
  return {
    title: (
      <>
        Set up your Clarix in <span className={GRADIENT_TEXT}>minutes,</span>
        <br />
        not months.
      </>
    ),
    body: `One workspace for ${APP_LIST}. Configure it the way your team actually works — no setup headaches.`,
    showStats: true,
  };
}

const STATS = [
  { value: '30 min', caption: 'avg. setup time' },
  { value: '99%', caption: 'automated onboarding' },
  { value: `${APP_NAMES.length} apps`, caption: 'in one shell' },
  { value: '4.9 / 5', caption: 'support rating' },
];

type Stage = 'idle' | 'leaving' | 'entering';

export function AuthHeadline() {
  const { pathname } = useLocation();
  const [copy, setCopy] = useState<Copy>(() => copyForPath(pathname));
  const [stage, setStage] = useState<Stage>('idle');

  // Derive-during-render: when path changes and produces new copy, kick
  // off the leave animation. (Avoids setState-in-effect.)
  const next = copyForPath(pathname);
  if (next.body !== copy.body && stage === 'idle') {
    setStage('leaving');
  }

  useEffect(() => {
    if (stage === 'leaving') {
      const t = window.setTimeout(() => {
        setCopy(copyForPath(pathname));
        setStage('entering');
      }, 220);
      return () => window.clearTimeout(t);
    }
    if (stage === 'entering') {
      const t = window.setTimeout(() => setStage('idle'), 280);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [stage, pathname]);

  const stageClass =
    stage === 'leaving'
      ? 'auth-headline-leaving opacity-0 -translate-y-2'
      : stage === 'entering'
        ? 'auth-headline-entering'
        : '';

  return (
    <div
      className={`relative z-10 transition-all duration-200 ease-out ${stageClass}`}
      style={
        stage === 'entering' ? { animation: 'auth-headline-in 280ms ease-out forwards' } : undefined
      }
    >
      <h1 className="text-8xl font-extrabold leading-[1.05] tracking-tight text-slate-900 dark:text-slate-100">
        {copy.title}
      </h1>
      <p className="mt-6 max-w-lg text-2xl leading-relaxed text-slate-600 dark:text-slate-300">
        {copy.body}
      </p>
      {copy.showStats && (
        <div className="mt-14 grid grid-cols-4 gap-2">
          {STATS.map((s) => (
            <div key={s.caption}>
              <div className="text-5xl font-bold text-slate-900 lg:text-6xl dark:text-slate-100">
                {s.value}
              </div>
              <div className="mt-1 text-lg text-slate-500 dark:text-slate-400">{s.caption}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
